/** Easing: presets as cubic-bezier control points, plus a deterministic bezier solver. */
import type { Easing, EasingPreset } from '@prism-graphics/scene-schema';

type Bezier = readonly [number, number, number, number];

const PRESETS: Record<Exclude<EasingPreset, 'linear' | 'hold'>, Bezier> = {
  ease: [0.25, 0.1, 0.25, 1],
  easeIn: [0.42, 0, 1, 1],
  easeOut: [0, 0, 0.58, 1],
  easeInOut: [0.42, 0, 0.58, 1],
  easeInQuad: [0.11, 0, 0.5, 0],
  easeOutQuad: [0.5, 1, 0.89, 1],
  easeInOutQuad: [0.45, 0, 0.55, 1],
  easeInCubic: [0.32, 0, 0.67, 0],
  easeOutCubic: [0.33, 1, 0.68, 1],
  easeInOutCubic: [0.65, 0, 0.35, 1],
  easeInQuart: [0.5, 0, 0.75, 0],
  easeOutQuart: [0.25, 1, 0.5, 1],
  easeInOutQuart: [0.76, 0, 0.24, 1],
  easeInExpo: [0.7, 0, 0.84, 0],
  easeOutExpo: [0.16, 1, 0.3, 1],
  easeInOutExpo: [0.87, 0, 0.13, 1],
  easeInBack: [0.36, 0, 0.66, -0.56],
  easeOutBack: [0.34, 1.56, 0.64, 1],
  easeInOutBack: [0.68, -0.6, 0.32, 1.6],
};

function bezierAt(p1: number, p2: number, t: number): number {
  const u = 1 - t;
  return 3 * u * u * t * p1 + 3 * u * t * t * p2 + t * t * t;
}

function bezierSlope(p1: number, p2: number, t: number): number {
  const u = 1 - t;
  return 3 * u * u * p1 + 6 * u * t * (p2 - p1) + 3 * t * t * (1 - p2);
}

/** y(x) of a CSS-style cubic bezier, x in [0, 1]. Newton steps with a bisection fallback; no randomness. */
export function cubicBezier(b: Bezier, x: number): number {
  const [x1, y1, x2, y2] = b;
  if (x <= 0) return 0;
  if (x >= 1) return 1;
  let t = x;
  for (let i = 0; i < 8; i++) {
    const err = bezierAt(x1, x2, t) - x;
    if (Math.abs(err) < 1e-7) return bezierAt(y1, y2, t);
    const d = bezierSlope(x1, x2, t);
    if (Math.abs(d) < 1e-6) break;
    t -= err / d;
  }
  let lo = 0;
  let hi = 1;
  t = x;
  for (let i = 0; i < 40; i++) {
    const v = bezierAt(x1, x2, t);
    if (Math.abs(v - x) < 1e-7) break;
    if (v < x) lo = t;
    else hi = t;
    t = (lo + hi) / 2;
  }
  return bezierAt(y1, y2, t);
}

/** The cubic-bezier control points of an easing (linear is [0, 0, 1, 1]); undefined for `hold`. */
export function bezierOf(easing: Easing | undefined): Bezier | undefined {
  if (easing === undefined || easing === 'linear') return [0, 0, 1, 1];
  if (easing === 'hold') return undefined;
  return typeof easing === 'string' ? PRESETS[easing] : easing.cubicBezier;
}

/** Apply an easing to progress p in [0, 1]. */
export function ease(easing: Easing | undefined, p: number): number {
  if (p <= 0) return 0;
  if (p >= 1) return 1;
  if (easing === undefined || easing === 'linear') return p;
  if (easing === 'hold') return 0;
  if (typeof easing === 'string') return cubicBezier(PRESETS[easing], p);
  return cubicBezier(easing.cubicBezier, p);
}
